import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import SyncFiltersWithURL from 'components/admin/SyncFiltersWithUrl'
import CoverageAuditLogsFiltersView, {
  type CoverageAuditLogFilterGroupType,
  filterConfigurations,
} from 'components/brokers/coverage_audit_logs/CoverageAuditLogsFiltersView'
import CoverageAuditLogsQuickFilters from 'components/brokers/coverage_audit_logs/CoverageAuditLogsQuickFilters'
import CoverageAuditLogsTable, {
  type CoverageAuditLogsFilters,
  getCoverageAuditLogsQueryKey
} from 'components/brokers/coverage_audit_logs/CoverageAuditLogsTable'
import BrokerContext from 'components/context/BrokerContext'
import CoverageAuditLogsContext from 'components/context/CoverageAuditLogsContext'
import { type BrokersBroker, type BrokersCoverageAuditLog } from 'utils/web_api.schemas'

type CoverageAuditLogsViewProps = {
  availableReasons?: string[]
  currentBroker?: BrokersBroker
}

const CoverageAuditLogsViewImpl = ({
  availableReasons,
  currentBroker,
}: CoverageAuditLogsViewProps) => {
  const [filters, setFilters] = useState<CoverageAuditLogsFilters>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 5,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])

  return (
    <CoverageAuditLogsContext.Provider
      value={{
        getCoverageAuditLogsQueryKey: getCoverageAuditLogsQueryKey({
          filters,
          pagination,
          sorting,
        }),
      }}
    >
      <BrokerContext.Provider value={{ currentBroker }}>
        <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
          <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
            <div className='text-2xl font-semibold w-96'>Coverage Audit Logs</div>
          </div>
          <CoverageAuditLogsQuickFilters
            filters={filters}
            setFilters={setFilters}
          />
          <CoverageAuditLogsFiltersView
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            availableReasons={availableReasons}
          />
          <SyncFiltersWithURL<
            CoverageAuditLogsFilters,
            BrokersCoverageAuditLog,
            CoverageAuditLogFilterGroupType
          >
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            sortableKeys={['created_at', 'action', 'reason']}
            filterConfigurations={filterConfigurations}
          />
          <CoverageAuditLogsTable
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            availableReasons={availableReasons}
          />
        </div>
      </BrokerContext.Provider>
    </CoverageAuditLogsContext.Provider>
  )
}

const CoverageAuditLogsView = (props: CoverageAuditLogsViewProps) => {
  const queryClient = new QueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <CoverageAuditLogsViewImpl {...props} />
    </QueryClientProvider>
  )
}

export default CoverageAuditLogsView
